import { useEffect, useMemo, useState } from "react";
import { CopyCode } from "@/components/CopyCode";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Trash2, Send, Circle, Lock, LockOpen, PawPrint, Hash, Dice5,
  RotateCcw, Ban, XCircle, Search, AlertTriangle, Check, Printer, Sparkles,
  LayoutGrid, List, Pin, PinOff, Zap,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Keypad } from "@/components/pos/Keypad";
import { TicketReceiptDialog } from "@/components/pos/TicketReceiptDialog";
import ShortcutsHelpOverlay from "@/components/pos/ShortcutsHelpOverlay";
import ShortcutStatusBar from "@/components/pos/ShortcutStatusBar";
import { formatBs, formatUsd, formatCop, formatTime } from "@/lib/format";
import { useAuth } from "@/lib/auth-context";
import { CloseSessionDialog } from "@/components/pos/CloseSessionDialog";
import { SlipAmountInput } from "@/components/pos/SlipAmountInput";
import { ChronoSorteoList } from "@/components/pos/ChronoSorteoList";
import { PosLockedOverlay } from "@/components/layout/PosShell";

const ZODIAC_SIGNS = [
  "Aries", "Tauro", "Géminis", "Cáncer", "Leo", "Virgo",
  "Libra", "Escorpio", "Sagitario", "Capricornio", "Acuario", "Piscis",
];

const familyIcon = (family) => {
  if (family === "animalitos") return PawPrint;
  if (family === "chance") return Dice5;
  return Hash;
};

const playKey = (sorteoId, number) => `${sorteoId}:${number}`;

// Números jugados SIEMPRE como string: '0' y '00' son jugadas distintas (sin parseInt/Number).
const NUMBER_SEP_RE = /[\s,.;]+/;
const parseNumbers = (raw) => String(raw || "").split(NUMBER_SEP_RE).filter(Boolean);
const normNumber = (n, len) => (n === "0" ? "0" : n.padStart(len, "0").slice(-len));
// Serie dX: d0..d9 (triples: 0d0..0d9); '0'/'00'/'000' nunca entran en una serie.
const seriesNumbers = (d, len) => Array.from({ length: 10 }, (_, i) => `${len === 3 ? "0" : ""}${d}${i}`).filter((n) => !/^0+$/.test(n));
const QUICK_ANIMALS = ["0", "00", ...Array.from({ length: 36 }, (_, i) => String(i + 1).padStart(2, "0"))];

// Turno del sorteo según su hora (Caracas): Sec (madrugada) · Mañ · Tar · Noc
const TURNS = [
  { key: "all", label: "Todos" },
  { key: "sec", label: "Sec" },
  { key: "man", label: "Mañ" },
  { key: "tar", label: "Tar" },
  { key: "noc", label: "Noc" },
];
function turnOf(scheduledAt) {
  try {
    const h = Number(new Intl.DateTimeFormat("en-US", { hour: "2-digit", hour12: false, timeZone: "America/Caracas" }).format(new Date(scheduledAt)));
    if (h < 9) return "sec";
    if (h < 13) return "man";
    if (h < 18) return "tar";
    return "noc";
  } catch { return "man"; }
}

// Fecha local de Venezuela (UTC-4). Regla: solo jugadas del día (la tasa BCV varía a diario).
const CARACAS_OFFSET_MS = -4 * 60 * 60 * 1000;
const caracasDateStr = (d) => new Date(d.getTime() + CARACAS_OFFSET_MS).toISOString().slice(0, 10);
const isTodayCaracas = (iso) => {
  if (!iso) return false;
  return caracasDateStr(new Date(iso)) === caracasDateStr(new Date());
};

export default function PosSalesPage() {
  const qc = useQueryClient();
  const { user } = useAuth();

  const [selectedLotteryIds, setSelectedLotteryIds] = useState([]);
  const [selectedSorteoIds, setSelectedSorteoIds] = useState([]);
  const [numberInput, setNumberInput] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [selectedSigns, setSelectedSigns] = useState([]);
  const [signPinned, setSignPinned] = useState(false);
  const [burstMode, setBurstMode] = useState(false);
  const [activeField, setActiveField] = useState("number");
  const [currency, setCurrency] = useState("BS");
  const [lotteryView, setLotteryView] = useState("grid"); // 'grid' | 'list'
  const [turnFilter, setTurnFilter] = useState("all"); // filtro de turno (Sec/Mañ/Tar/Noc/Todos)
  const [showKeypad, setShowKeypad] = useState(true);
  const [plays, setPlays] = useState([]);
  const [selectedPlayIdx, setSelectedPlayIdx] = useState(null);
  const [agotadoKeys, setAgotadoKeys] = useState(new Set());
  const [customer, setCustomer] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [receiptReprint, setReceiptReprint] = useState(false);
  const [receiptSorteoMap, setReceiptSorteoMap] = useState(null);
  const [validating, setValidating] = useState(false);

  const [openSession, setOpenSession] = useState(false);
  const [closeSession, setCloseSession] = useState(false);
  const [sessionForm, setSessionForm] = useState({ opening_bs: 0, opening_usd: 0, opening_cop: 0, closing_bs: 0, closing_usd: 0, closing_cop: 0, notes: "" });

  const [slipPage, setSlipPage] = useState(0);
  const SLIP_PAGE_SIZE = 50;
  const SLIP_MAX_PAGES = 4;
  const SLIP_MAX_PLAYS = SLIP_PAGE_SIZE * SLIP_MAX_PAGES; // 200

  const [availDialog, setAvailDialog] = useState({ open: false, results: [] });
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [repeatCode, setRepeatCode] = useState("");
  const [repeatTicket, setRepeatTicket] = useState(null);
  const [voidSerialOpen, setVoidSerialOpen] = useState(false);
  const [voidSerialCode, setVoidSerialCode] = useState("");
  const [reprintOpen, setReprintOpen] = useState(false);
  const [reprintCode, setReprintCode] = useState("");

  // Sistema de atajos avanzado (Windows/Web POS)
  const [showHelp, setShowHelp] = useState(false);
  const [flashKey, setFlashKey] = useState(null);
  const [lastAmount, setLastAmount] = useState("");
  const [confirmVoidLast, setConfirmVoidLast] = useState(false);
  const flash = (k) => {
    setFlashKey(k);
    setTimeout(() => setFlashKey((cur) => (cur === k ? null : cur)), 240);
  };

  const { data: session } = useQuery({
    queryKey: ["cash-session"],
    queryFn: async () => (await api.get("/cash-sessions/current")).data,
    refetchInterval: 20000,
  });
  const { data: taqStatus } = useQuery({
    queryKey: ["taquilla-me-status"],
    queryFn: async () => (await api.get("/taquillas/me/status")).data,
    refetchInterval: 30000,
  });
  const { data: rate } = useQuery({
    queryKey: ["rate", "current"],
    queryFn: async () => (await api.get("/exchange-rates/current")).data,
  });
  const { data: lotteries = [] } = useQuery({
    queryKey: ["lottery-types", "active"],
    queryFn: async () => (await api.get("/lottery-types?active=true")).data,
  });
  const { data: sorteos = [] } = useQuery({
    queryKey: ["sorteos", "available"],
    queryFn: async () => (await api.get("/sorteos/available")).data,
    refetchInterval: 30000,
    // Regla operativa: solo jugadas del día (filtro defensivo en cliente)
    select: (rows) => rows.filter((s) => isTodayCaracas(s.scheduled_at)),
  });

  const lotteryMap = useMemo(() => Object.fromEntries(lotteries.map((l) => [l.id, l])), [lotteries]);
  const sorteoMap = useMemo(() => Object.fromEntries(sorteos.map((s) => [s.id, s])), [sorteos]);

  // Auto-seleccionar la primera lotería al cargar
  useEffect(() => {
    if (selectedLotteryIds.length === 0 && lotteries.length > 0) {
      setSelectedLotteryIds([lotteries[0].id]);
    }
  }, [lotteries]); // eslint-disable-line

  // Mantener coherente la selección de sorteos con las loterías activas
  useEffect(() => {
    setSelectedSorteoIds((prev) => {
      const next = prev.filter((sid) => {
        const s = sorteoMap[sid];
        return s && selectedLotteryIds.includes(s.lottery_type_id);
      });
      return next.length === prev.length ? prev : next;
    });
  }, [selectedLotteryIds, sorteoMap]);

  // Mantener slipPage dentro del rango cuando cambian las jugadas
  useEffect(() => {
    const maxPage = plays.length === 0 ? 0 : Math.min(SLIP_MAX_PAGES - 1, Math.floor((plays.length - 1) / SLIP_PAGE_SIZE));
    if (slipPage > maxPage) setSlipPage(maxPage);
  }, [plays.length, slipPage]);

  const sorteosForSelected = useMemo(
    () => sorteos.filter((s) => selectedLotteryIds.includes(s.lottery_type_id)),
    [sorteos, selectedLotteryIds]
  );

  // longitud de número: la MAYOR entre las loterías seleccionadas (evita truncar/rellenar en silencio)
  const primaryLottery = lotteryMap[selectedLotteryIds[0]];
  const numberLen = useMemo(() => {
    const lens = selectedLotteryIds.map((id) => lotteryMap[id]?.number_length).filter(Boolean);
    return lens.length ? Math.max(...lens) : 3;
  }, [selectedLotteryIds, lotteryMap]);

  // ¿alguna lotería seleccionada requiere signo zodiacal?
  const needsSign = useMemo(
    () => selectedLotteryIds.some((id) => lotteryMap[id]?.has_zodiac_sign),
    [selectedLotteryIds, lotteryMap]
  );

  const needsSession = user?.role !== "admin" && !session;
  const showQuickGrid = numberLen === 2 && selectedLotteryIds.some((id) => {
    const l = lotteryMap[id];
    return l?.family === "animalitos" || /GRANJ/i.test(l?.code || "");
  });

  const resetSign = () => { setSelectedSigns([]); setSignPinned(false); };
  const toggleSign = (sign) => setSelectedSigns((prev) => prev.includes(sign) ? prev.filter((x) => x !== sign) : [...prev, sign]);
  const toggleLottery = (id) => {
    resetSign();
    setSelectedLotteryIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSorteo = (id) => {
    setSelectedSorteoIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  // Modo Lista: alterna el sorteo y mantiene coherente su lotería en la selección
  const toggleSorteoFromList = (s) => {
    const selecting = !selectedSorteoIds.includes(s.id);
    const nextSorteos = selecting ? [...selectedSorteoIds, s.id] : selectedSorteoIds.filter((x) => x !== s.id);
    const lotteryStillUsed = nextSorteos.some((sid) => sorteoMap[sid]?.lottery_type_id === s.lottery_type_id);
    if (selecting ? !selectedLotteryIds.includes(s.lottery_type_id) : !lotteryStillUsed) resetSign();
    setSelectedLotteryIds((prev) => {
      if (selecting) return prev.includes(s.lottery_type_id) ? prev : [...prev, s.lottery_type_id];
      return lotteryStillUsed ? prev : prev.filter((x) => x !== s.lottery_type_id);
    });
    setSelectedSorteoIds(nextSorteos);
  };
  const listSel = useMemo(() => {
    const sel = selectedSorteoIds.map((id) => sorteoMap[id]).filter(Boolean);
    return { lotteries: new Set(sel.map((s) => s.lottery_type_id)).size, hours: new Set(sel.map((s) => s.scheduled_at)).size };
  }, [selectedSorteoIds, sorteoMap]);
  const allSorteosSelected = sorteosForSelected.length > 0 && sorteosForSelected.every((s) => selectedSorteoIds.includes(s.id));
  const toggleAllSorteos = () => {
    if (allSorteosSelected) setSelectedSorteoIds([]);
    else setSelectedSorteoIds(sorteosForSelected.map((s) => s.id));
  };

  // ------ teclado físico (POS profesional: F-keys + Tab + flechas + Ctrl+N + decimal) ------
  useEffect(() => {
    const handler = (e) => {
      const k = e.key;

      // F1 y Esc siempre disponibles (incluso dentro de diálogos)
      if (k === "F1") {
        e.preventDefault();
        setShowHelp((v) => !v);
        flash("f1");
        return;
      }
      if (k === "Escape") {
        if (showHelp) { e.preventDefault(); setShowHelp(false); return; }
        // dejar que los diálogos cierren por su cuenta
      }

      // No interceptar el resto si hay diálogo abierto o foco en campo editable
      if (document.querySelector('[role="dialog"]')) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.target?.isContentEditable || e.target?.getAttribute?.("role") === "combobox") return;
      if (taqStatus?.suspended) return; // gating: atajos inhabilitados con taquilla bloqueada

      // F-keys operativos
      if (k === "F2") { e.preventDefault(); flash("f2"); const order = ["BS", "USD", "COP"]; changeCurrency(order[(order.indexOf(currency) + 1) % order.length]); return; }
      if (k === "F3") { e.preventDefault(); flash("f3"); setRepeatTicket(null); setRepeatCode(""); setRepeatOpen(true); return; }
      if (k === "F4") { e.preventDefault(); flash("f4"); setReprintCode(""); setReprintOpen(true); return; }
      if (k === "F5") { e.preventDefault(); flash("f5"); setConfirmVoidLast(true); return; }
      if (k === "F6") { e.preventDefault(); flash("f6"); setVoidSerialCode(""); setVoidSerialOpen(true); return; }
      if (k === "F7") {
        e.preventDefault(); flash("f7");
        setSessionForm({ opening_bs: 0, opening_usd: 0, opening_cop: 0, closing_bs: 0, closing_usd: 0, closing_cop: 0, notes: "" });
        if (session) setCloseSession(true); else setOpenSession(true);
        return;
      }
      if (k === "F8") { e.preventDefault(); flash("f8"); handleEmit(); return; }
      if (k === "F9") { e.preventDefault(); flash("f9"); setLotteryView((v) => (v === "grid" ? "list" : "grid")); return; }
      if (k === "F10") { e.preventDefault(); flash("f10"); clearSlip(); return; }
      if (k === "F12") {
        e.preventDefault();
        if (needsSign) {
          flash("f12");
          document.querySelector('[data-testid="pos-sign-selector"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }

      // Ctrl + 1..9 → alternar lotería N° 1-9
      if (e.ctrlKey && !e.altKey && !e.metaKey && /^[1-9]$/.test(k)) {
        e.preventDefault();
        const idx = parseInt(k, 10) - 1;
        if (lotteries[idx]) { flash(`lot${idx}`); toggleLottery(lotteries[idx].id); }
        return;
      }

      // Tab → alternar Número ↔ Monto
      if (k === "Tab") { e.preventDefault(); flash("tab"); setActiveField((f) => (f === "number" ? "amount" : "number")); return; }

      // Delete → eliminar jugada seleccionada
      if (k === "Delete") { e.preventDefault(); removeSelectedPlay(); return; }

      // Flechas ↑/↓ → navegar por las jugadas
      if (k === "ArrowUp" && plays.length) {
        e.preventDefault();
        setSelectedPlayIdx((i) => {
          const next = i == null ? plays.length - 1 : Math.max(0, i - 1);
          setSlipPage(Math.min(SLIP_MAX_PAGES - 1, Math.floor(next / SLIP_PAGE_SIZE)));
          return next;
        });
        return;
      }
      if (k === "ArrowDown" && plays.length) {
        e.preventDefault();
        setSelectedPlayIdx((i) => {
          const next = i == null ? 0 : Math.min(plays.length - 1, i + 1);
          setSlipPage(Math.min(SLIP_MAX_PAGES - 1, Math.floor(next / SLIP_PAGE_SIZE)));
          return next;
        });
        return;
      }

      // Separadores de lista en Número (coma, punto o espacio) → carga en ráfaga
      if ((k === "," || k === "." || k === " ") && activeField === "number") {
        e.preventDefault();
        pressSeparator();
        return;
      }

      // Decimal en Monto
      if ((k === "." || k === ",") && activeField === "amount" && !amountInput.includes(".")) {
        e.preventDefault();
        setAmountInput((v) => (v ? v : "0") + ".");
        return;
      }

      // Atajos existentes
      if (k >= "0" && k <= "9") pressKey(k);
      else if (k === "Backspace") pressBackspace();
      // Tecla + (NumpadAdd): emitir e imprimir directamente
      else if (k === "+" || k === "Add") { e.preventDefault(); flash("f8"); handleEmit(); }
      // Enter: alterna foco Número↔Monto e inserta la jugada al ticket
      else if (k === "Enter") {
        e.preventDefault();
        if (activeField === "number" && numberInput && !(parseFloat(amountInput) > 0)) { flash("tab"); setActiveField("amount"); }
        else { flash("add"); addPlay(); }
      }
      else if (k.toLowerCase() === "e") { e.preventDefault(); removeSelectedPlay(); }
      else if (k.toLowerCase() === "l") { e.preventDefault(); clearSlip(); }
      else if (k.toLowerCase() === "t") setActiveField("amount");
      else if (k.toLowerCase() === "n") setActiveField("number");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }); // re-bind each render to capture latest state

  const burstReady = burstMode && parseFloat(amountInput) > 0;
  const pressKey = (k) => {
    if (activeField === "number") {
      const tokens = numberInput.split(NUMBER_SEP_RE);
      const last = tokens[tokens.length - 1] || "";
      if (last.length >= numberLen) return;
      const next = numberInput + k;
      // Ráfaga: al completar los dígitos de la lotería se agrega de inmediato con el monto fijado
      if (burstReady && last.length + 1 === numberLen) { addPlay(next); return; }
      setNumberInput(next);
    } else setAmountInput(amountInput + k);
  };
  const pressSeparator = () => {
    if (activeField !== "number") return;
    if (numberInput && !/[\s,.;]$/.test(numberInput)) setNumberInput(numberInput + ", ");
  };
  const pickQuickNumber = (n) => {
    if (burstReady) return addPlay(n);
    setActiveField("number");
    setNumberInput((v) => (v && !/[\s,.;]$/.test(v) ? `${v}, ${n}` : `${v}${n}`));
  };
  const pickSeries = (d) => {
    const nums = seriesNumbers(d, numberLen).join(", ");
    if (burstReady) return addPlay(nums);
    setActiveField("number");
    setNumberInput((v) => (!v ? nums : /[\s,.;]$/.test(v) ? `${v}${nums}` : `${v}, ${nums}`));
  };
  const pressBackspace = () => {
    if (activeField === "number") setNumberInput(numberInput.slice(0, -1));
    else setAmountInput(amountInput.slice(0, -1));
  };
  const pressClear = () => {
    if (activeField === "number") setNumberInput("");
    else setAmountInput("");
  };

  const totals = useMemo(() => {
    let bs = 0, usd = 0, cop = 0;
    for (const p of plays) { bs += p.amount_bs; usd += p.amount_usd; cop += (p.amount_cop || 0); }
    return { bs: Math.round(bs * 100) / 100, usd: Math.round(usd * 100) / 100, cop: Math.round(cop) };
  }, [plays]);

  const rateVal = rate?.bs_per_usd || 40;
  const copRateVal = rate?.bs_per_cop || 0.01;

  // Bloqueo de divisa cuando ya hay jugadas (evita mezclar monedas en un ticket)
  const currencyLocked = plays.length > 0;
  const changeCurrency = (c) => {
    if (currencyLocked) { toast.error("No puedes cambiar la moneda con jugadas en el ticket. Limpia el ticket primero."); return; }
    setCurrency(c);
  };
  // Tema dinámico por divisa: BS = Azul corporativo, USD = Verde esmeralda, COP = Vinotinto
  const isUsd = currency === "USD";
  const isCop = currency === "COP";
  const curTheme = isCop ? {
    border: "border-[#8f1433]",
    ring: "ring-[#8f1433]/50",
    text: "text-rose-300",
    btn: "bg-[#8f1433] hover:bg-[#a81a3f] text-white",
    bgSoft: "bg-[#8f1433]/10",
  } : isUsd ? {
    border: "border-emerald-500",
    ring: "ring-emerald-500/50",
    text: "text-emerald-300",
    btn: "bg-emerald-600 hover:bg-emerald-500 text-white",
    bgSoft: "bg-emerald-500/10",
  } : {
    border: "border-sky-500",
    ring: "ring-sky-500/50",
    text: "text-sky-300",
    btn: "bg-sky-600 hover:bg-sky-500 text-white",
    bgSoft: "bg-sky-500/10",
  };

  // Convierte un monto en la divisa activa a sus equivalentes (bs/usd/cop)
  const computeAmounts = (amt) => {
    let amount_bs;
    if (currency === "USD") amount_bs = Math.round(amt * rateVal * 100) / 100;
    else if (currency === "COP") amount_bs = Math.round(amt * copRateVal * 100) / 100;
    else amount_bs = amt;
    const amount_usd = Math.round((amount_bs / rateVal) * 100) / 100;
    const amount_cop = currency === "COP" ? Math.round(amt) : (copRateVal > 0 ? Math.round(amount_bs / copRateVal) : 0);
    return { amount_bs, amount_usd, amount_cop };
  };

  const updatePlayAmount = (idx, amt) => {
    setPlays((prev) => prev.map((p, i) => (i === idx ? { ...p, ...computeAmounts(amt) } : p)));
  };

  const addPlay = (rawNumbers) => {
    if (selectedSorteoIds.length === 0) return toast.error("Selecciona al menos un horario/sorteo");
    const numbers = parseNumbers(typeof rawNumbers === "string" ? rawNumbers : numberInput);
    if (numbers.length === 0) return toast.error("Ingresa un número");
    const badNum = numbers.find((n) => !/^\d+$/.test(n) || n.length > numberLen);
    if (badNum) return toast.error(`Número inválido: ${badNum}`);
    // Autocompletar con el último monto si el campo está vacío ("modo turbo")
    let amountRaw = amountInput;
    if ((!amountRaw || parseFloat(amountRaw) <= 0) && lastAmount && parseFloat(lastAmount) > 0) {
      amountRaw = lastAmount;
    }
    if (!amountRaw || parseFloat(amountRaw) <= 0) return toast.error("Ingresa un monto");
    // Si alguna lotería seleccionada exige signo, valida que se haya elegido
    const anyZodiacSelected = selectedSorteoIds.some((sid) => {
      const s = sorteoMap[sid];
      return s && lotteryMap[s.lottery_type_id]?.has_zodiac_sign;
    });
    if (anyZodiacSelected && selectedSigns.length === 0) return toast.error("Selecciona un signo zodiacal");
    const amt = parseFloat(amountRaw);
    const newPlays = [];
    for (const num of numbers) {
      for (const sid of selectedSorteoIds) {
        const s = sorteoMap[sid];
        if (!s) continue;
        const lot = lotteryMap[s.lottery_type_id];
        const len = lot?.number_length || 3;
        for (const sign of lot?.has_zodiac_sign ? selectedSigns : [null]) {
          newPlays.push({
            lottery_type_id: s.lottery_type_id, sorteo_id: sid, number: normNumber(num, len),
            sign, ...computeAmounts(amt), currency,
          });
        }
      }
    }
    const spaceLeft = SLIP_MAX_PLAYS - plays.length;
    if (spaceLeft <= 0) {
      return toast.error(`Límite alcanzado: ${SLIP_MAX_PLAYS} jugadas por ticket`);
    }
    let trimmed = newPlays;
    let overflow = 0;
    if (newPlays.length > spaceLeft) {
      trimmed = newPlays.slice(0, spaceLeft);
      overflow = newPlays.length - spaceLeft;
    }
    const finalPlays = [...plays, ...trimmed];
    setPlays(finalPlays);
    // Saltar a la página que contiene la última jugada agregada
    const lastIdx = finalPlays.length - 1;
    setSlipPage(Math.min(SLIP_MAX_PAGES - 1, Math.floor(lastIdx / SLIP_PAGE_SIZE)));
    setLastAmount(String(amountRaw));
    setNumberInput("");
    if (!burstMode) setAmountInput("");
    if (!signPinned) setSelectedSigns([]);
    setActiveField("number");
    const usedLast = !amountInput && lastAmount;
    toast.success(
      (trimmed.length > 1 ? `${trimmed.length} jugadas agregadas` : "Jugada agregada") +
      (usedLast ? ` · monto ${lastAmount} reutilizado` : "") +
      (overflow ? ` · ${overflow} descartadas (tope ${SLIP_MAX_PLAYS})` : "")
    );
  };

  const removePlay = (idx) => {
    setPlays(plays.filter((_, i) => i !== idx));
    setSelectedPlayIdx(null);
  };
  const removeSelectedPlay = () => {
    if (plays.length === 0) return;
    const idx = selectedPlayIdx != null && selectedPlayIdx < plays.length ? selectedPlayIdx : plays.length - 1;
    setPlays(plays.filter((_, i) => i !== idx));
    setSelectedPlayIdx(null);
  };
  const clearSlip = () => {
    if (plays.length === 0) return;
    setPlays([]);
    setSelectedPlayIdx(null);
    setSlipPage(0);
    clearAgotado();
  };
  const clearAgotado = () => setAgotadoKeys(new Set());

  // Limpieza post-venta: purga slip, entradas y (full) selección de loterías/horarios
  const clearFormState = (full = true) => {
    setPlays([]);
    setSelectedPlayIdx(null);
    setSlipPage(0);
    setNumberInput("");
    if (!burstMode) setAmountInput("");
    if (!signPinned) setSelectedSigns([]);
    setCustomer("");
    setActiveField("number");
    clearAgotado();
    // Con signo fijado se conserva la lotería/horarios para la siguiente venta
    if (full && !signPinned) {
      setSelectedSorteoIds([]);
      setSelectedLotteryIds([]);
    }
  };

  const buildPayload = () => ({
    currency,
    customer_name: customer || null,
    plays: plays.map((p) => ({ sorteo_id: p.sorteo_id, number: p.number, amount_bs: p.amount_bs, sign: p.sign || null })),
  });

  // Antifraude: 422 security_review → toast destructivo + refresco inmediato del estado (overlay de suspensión).
  const handleSecurityReview = (e) => {
    const data = e?.response?.data;
    if (e?.response?.status !== 422 || data?.reason !== "security_review") return false;
    toast.error(data.message, { duration: 12000, id: "security-review" });
    const hold = (old) => (old ? { ...old, suspended: true, suspended_reason: "taquilla" } : old);
    qc.setQueriesData({ queryKey: ["taquilla-status"] }, hold);
    qc.setQueriesData({ queryKey: ["taquilla-me-status"] }, hold);
    qc.invalidateQueries({ queryKey: ["taquilla-me-status"] });
    qc.invalidateQueries({ queryKey: ["taquilla-status"] });
    return true;
  };

  const sell = useMutation({
    mutationFn: async () => (await api.post("/tickets", buildPayload())).data,
    onSuccess: (t) => {
      setReceiptReprint(false);
      setReceiptSorteoMap(null);
      setReceipt(t);
      clearFormState(true);
      setAvailDialog({ open: false, results: [] });
      qc.invalidateQueries({ queryKey: ["cash-session"] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
      toast.success("Ticket emitido");
    },
    onError: (e) => {
      if (handleSecurityReview(e)) return;
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.agotado) {
        const keys = new Set(detail.agotado.map((a) => playKey(a.sorteo_id, a.number)));
        setAgotadoKeys(keys);
        setAvailDialog({ open: true, results: detail.agotado });
      } else {
        toast.error(typeof detail === "string" ? detail : "Error al emitir");
      }
    },
  });

  // Emitir: primero pre-valida disponibilidad ("agotado") ANTES de imprimir
  const handleEmit = async () => {
    if (plays.length === 0) return;
    if (taqStatus?.suspended) { toast.error("Taquilla suspendida — emisión de tickets bloqueada"); return; }
    clearAgotado();
    setValidating(true);
    try {
      const res = await api.post("/tickets/validate-availability", buildPayload());
      if (res.data.reason === "limit_exceeded") {
        toast.error("Límite de venta alcanzado. Emisión no permitida. Contacte a su supervisor.");
        return;
      }
      if (res.data.ok) {
        sell.mutate();
      } else {
        const agot = res.data.results.filter((r) => !r.available);
        setAgotadoKeys(new Set(agot.map((r) => playKey(r.sorteo_id, r.number))));
        setAvailDialog({ open: true, results: agot });
      }
    } catch (e) {
      if (handleSecurityReview(e)) return;
      toast.error(e?.response?.data?.detail || "Error al validar disponibilidad");
    } finally {
      setValidating(false);
    }
  };

  const removeAgotadasAndEmit = () => {
    const remaining = plays.filter((p) => !agotadoKeys.has(playKey(p.sorteo_id, p.number)));
    setPlays(remaining);
    setAvailDialog({ open: false, results: [] });
    clearAgotado();
    if (remaining.length === 0) {
      toast.message("Todas las jugadas estaban agotadas. Ticket vacío.");
      return;
    }
    setTimeout(() => sell.mutate(), 50);
  };

  // ------ Repetir por serial ------
  const findRepeat = async () => {
    if (!repeatCode.trim()) return;
    try {
      const t = (await api.get(`/tickets/by-code/${repeatCode.trim().toUpperCase()}`)).data;
      setRepeatTicket(t);
    } catch {
      toast.error("Ticket no encontrado");
      setRepeatTicket(null);
    }
  };
  const loadRepeatPlays = () => {
    if (!repeatTicket) return;
    let loaded = 0, skipped = 0;
    const additions = [];
    for (const p of repeatTicket.plays) {
      const target = sorteos
        .filter((s) => s.lottery_type_id === p.lottery_type_id)
        .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
      if (!target) { skipped++; continue; }
      additions.push({
        lottery_type_id: p.lottery_type_id,
        sorteo_id: target.id,
        number: p.number,
        sign: p.sign || null,
        amount_bs: p.amount_bs,
        amount_usd: Math.round((p.amount_bs / rateVal) * 100) / 100,
        currency: "BS",
      });
      loaded++;
    }
    setPlays((prev) => [...prev, ...additions]);
    setRepeatOpen(false);
    setRepeatCode("");
    setRepeatTicket(null);
    toast.success(`${loaded} jugadas cargadas${skipped ? ` · ${skipped} sin sorteo disponible` : ""}. Ajusta horas/loterías y emite.`);
  };

  const voidLast = useMutation({
    mutationFn: async () => (await api.post("/tickets/void-last")).data,
    onSuccess: (t) => { toast.success(`Ticket ${t.code} anulado`); qc.invalidateQueries({ queryKey: ["cash-session"] }); qc.invalidateQueries({ queryKey: ["tickets"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No hay tickets para anular"),
  });

  const voidBySerial = useMutation({
    mutationFn: async () => (await api.post(`/tickets/${voidSerialCode.trim().toUpperCase()}/void`)).data,
    onSuccess: (t) => { toast.success(`Ticket ${t.code} anulado`); setVoidSerialOpen(false); setVoidSerialCode(""); qc.invalidateQueries({ queryKey: ["cash-session"] }); qc.invalidateQueries({ queryKey: ["tickets"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo anular"),
  });

  // ------ Reimprimir por serial ------
  const doReprint = async () => {
    if (!reprintCode.trim()) return;
    try {
      const t = (await api.get(`/tickets/by-code/${reprintCode.trim().toUpperCase()}`)).data;
      // Resolver etiquetas de sorteos históricos (no presentes en /sorteos/available)
      try {
        const resp = (await api.get("/sorteos", { params: { limit: 200 } })).data;
        const all = Array.isArray(resp) ? resp : (resp.items || []);
        setReceiptSorteoMap({ ...sorteoMap, ...Object.fromEntries(all.map((s) => [s.id, s])) });
      } catch {
        setReceiptSorteoMap(null);
      }
      setReprintOpen(false);
      setReprintCode("");
      setReceiptReprint(true);
      setReceipt(t);
    } catch {
      toast.error("Ticket no encontrado");
    }
  };

  const closeReceipt = () => {
    setReceipt(null);
    setReceiptReprint(false);
    setReceiptSorteoMap(null);
  };

  const openSess = useMutation({
    mutationFn: async () => (await api.post("/cash-sessions/open", {
      opening_bs: parseFloat(sessionForm.opening_bs) || 0,
      opening_usd: parseFloat(sessionForm.opening_usd) || 0,
      opening_cop: parseFloat(sessionForm.opening_cop) || 0,
    })).data,
    onSuccess: () => { toast.success("Caja abierta"); qc.invalidateQueries({ queryKey: ["cash-session"] }); setOpenSession(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const closeSess = useMutation({
    mutationFn: async () => (await api.post("/cash-sessions/close", {
      closing_bs: parseFloat(sessionForm.closing_bs) || 0,
      closing_usd: parseFloat(sessionForm.closing_usd) || 0,
      closing_cop: parseFloat(sessionForm.closing_cop) || 0,
      notes: sessionForm.notes || null,
    })).data,
    onSuccess: () => { toast.success("Caja cerrada"); qc.invalidateQueries({ queryKey: ["cash-session"] }); setCloseSession(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  return (
    <div className="relative p-3 md:p-4 pb-16" style={{ "--primary": isUsd ? "160 84% 39%" : isCop ? "345 75% 32%" : "199 89% 48%", "--primary-foreground": "0 0% 100%", "--ring": isUsd ? "160 84% 39%" : isCop ? "345 75% 32%" : "199 89% 48%" }} data-testid="pos-currency-theme-wrap" data-currency={currency}>
      <PosLockedOverlay />
      {needsSession && !taqStatus?.suspended && (
        <Card className="bg-amber-500/10 border-amber-500/30 p-4 mb-4 flex items-center gap-3" data-testid="pos-need-session-banner">
          <Lock size={20} className="text-amber-300" />
          <div className="flex-1">
            <div className="font-medium text-amber-200">Caja cerrada</div>
            <div className="text-xs text-muted-foreground">Abre caja para comenzar a vender.</div>
          </div>
          <Button onClick={() => { setSessionForm({ opening_bs: 0, opening_usd: 0, opening_cop: 0, closing_bs: 0, closing_usd: 0, closing_cop: 0, notes: "" }); setOpenSession(true); }} data-testid="pos-open-session-button">
            <LockOpen size={16} className="mr-1.5" /> Abrir caja
          </Button>
        </Card>
      )}
      {session && user?.role !== "admin" && (
        <Card className="bg-secondary/40 border-border p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="pos-session-banner">
          <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 gap-1.5">
            <Circle size={8} className="fill-emerald-400" /> Caja abierta
          </Badge>
          <div className="text-xs text-muted-foreground">Desde {formatTime(session.created_at)}</div>
          <div className="ml-auto flex items-center gap-3 text-xs">
            {[
              { c: "BS", label: "Bs", fmt: (v) => formatBs(v), sales: session.total_sales_bs, pays: session.total_payouts_bs },
              { c: "USD", label: "USD", fmt: (v) => `$ ${formatUsd(v)}`, sales: session.total_sales_usd, pays: session.total_payouts_usd },
              { c: "COP", label: "COP", fmt: (v) => `$ ${formatCop(v)}`, sales: session.total_sales_cop, pays: session.total_payouts_cop },
            ].map((g) => (
              <div key={g.c} data-testid={`pos-session-drawer-${g.c.toLowerCase()}`} data-active={currency === g.c ? "true" : "false"}
                className={`rounded-md border px-2 py-1 transition-colors ${currency === g.c ? `${curTheme.border} ${curTheme.bgSoft}` : "border-border opacity-70"}`}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{g.label}</div>
                <div>Ventas <span className="font-mono tabular-nums text-foreground" data-testid={`pos-session-sales-${g.c.toLowerCase()}`}>{g.fmt(g.sales || 0)}</span></div>
                <div>Pagos <span className="font-mono tabular-nums text-amber-700 dark:text-amber-300" data-testid={`pos-session-payouts-${g.c.toLowerCase()}`}>{g.fmt(g.pays || 0)}</span></div>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => { setSessionForm({ opening_bs: 0, opening_usd: 0, opening_cop: 0, closing_bs: 0, closing_usd: 0, closing_cop: 0, notes: "" }); setCloseSession(true); }} data-testid="pos-close-session-button">
              <Lock size={14} className="mr-1.5" />Cerrar caja
            </Button>
          </div>
        </Card>
      )}

      {/* Barra de acciones rápidas */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="pos-quick-actions">
        <Button variant="outline" size="sm" onClick={() => { setRepeatOpen(true); setRepeatTicket(null); setRepeatCode(""); }} data-testid="pos-repeat-button"
          className={flashKey === "f3" ? "ring-2 ring-primary bg-primary/10" : ""}>
          <RotateCcw size={15} className="mr-1.5" /> Repetir por serial (F3)
        </Button>
        <Button variant="outline" size="sm" onClick={() => { setReprintOpen(true); setReprintCode(""); }} data-testid="pos-reprint-button"
          className={flashKey === "f4" ? "ring-2 ring-primary bg-primary/10" : ""}>
          <Printer size={15} className="mr-1.5" /> Reimprimir (F4)
        </Button>
        <Button variant="outline" size="sm" className={`text-rose-300 border-rose-500/30 hover:bg-rose-500/10 ${flashKey === "f5" ? "ring-2 ring-rose-400 bg-rose-500/15" : ""}`} onClick={() => setConfirmVoidLast(true)} disabled={voidLast.isPending} data-testid="pos-void-last-button">
          <Ban size={15} className="mr-1.5" /> Anular último (F5)
        </Button>
        <Button variant="outline" size="sm" className={`text-rose-300 border-rose-500/30 hover:bg-rose-500/10 ${flashKey === "f6" ? "ring-2 ring-rose-400 bg-rose-500/15" : ""}`} onClick={() => { setVoidSerialOpen(true); setVoidSerialCode(""); }} data-testid="pos-void-serial-button">
          <XCircle size={15} className="mr-1.5" /> Anular por serial (F6)
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowHelp(true)} data-testid="pos-help-button"
          className={`ml-auto ${flashKey === "f1" ? "ring-2 ring-primary bg-primary/10" : ""}`}>
          <span className="mr-1.5">?</span> Ayuda (F1)
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3 max-w-full">
        {/* Panel izquierdo: loterías con horarios INLINE debajo de cada una */}
        <div className="md:col-span-1 lg:col-span-3 min-w-0 space-y-3">
          <Card className="bg-card border-2 border-border p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Loterías y horarios</div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="pos-lottery-view-toggle">
                  <button onClick={() => setLotteryView("grid")} className={`p-1.5 ${lotteryView === "grid" ? "bg-primary/20 text-primary" : "text-muted-foreground"}`} title="Vista tarjetas" data-testid="pos-lottery-view-grid"><LayoutGrid size={14}/></button>
                  <button onClick={() => setLotteryView("list")} className={`p-1.5 ${lotteryView === "list" ? "bg-primary/20 text-primary" : "text-muted-foreground"}`} title="Vista lista" data-testid="pos-lottery-view-list"><List size={14}/></button>
                </div>
                {lotteryView === "grid" && sorteosForSelected.length > 0 && (
                  <button onClick={toggleAllSorteos} className="text-[11px] font-semibold text-primary hover:underline" data-testid="pos-toggle-all-sorteos">
                    {allSorteosSelected ? "Quitar horarios" : "Todos"}
                  </button>
                )}
              </div>
            </div>
            {/* Barra de turnos (Sec/Mañ/Tar/Noc/Todos) + limpiar filtro */}
            <div className="flex items-center gap-1 mb-2 flex-wrap" data-testid="pos-turn-bar">
              {TURNS.map((tt) => (
                <button key={tt.key} onClick={() => setTurnFilter(tt.key)} data-testid={`pos-turn-${tt.key}`}
                  className={`rounded-md border px-2 py-1 text-[11px] font-bold transition-colors ${turnFilter === tt.key ? "border-primary bg-primary/20 text-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                  {tt.label}
                </button>
              ))}
              <button onClick={() => setTurnFilter("all")} title="Limpiar filtro de turno" data-testid="pos-turn-clear"
                className="ml-auto grid place-items-center rounded-md border border-border p-1 text-muted-foreground hover:bg-secondary/60">
                <RotateCcw size={13}/>
              </button>
            </div>
            <div className="max-h-[calc(100vh-260px)] overflow-y-auto pr-1" data-testid="pos-lottery-scroll" style={{ scrollbarWidth: "thin" }}>
              {lotteryView === "list" ? (
                <ChronoSorteoList
                  sorteos={sorteos.filter((s) => turnFilter === "all" || turnOf(s.scheduled_at) === turnFilter)}
                  lotteryMap={lotteryMap}
                  selectedSorteoIds={selectedSorteoIds}
                  onToggle={toggleSorteoFromList}
                />
              ) : (
              <div className="grid grid-cols-1 gap-2">
                {lotteries.map((l) => {
                  const active = selectedLotteryIds.includes(l.id);
                  const Icon = familyIcon(l.family);
                  const group = sorteos.filter((s) => s.lottery_type_id === l.id && (turnFilter === "all" || turnOf(s.scheduled_at) === turnFilter));
                  const allGroupSel = group.length > 0 && group.every((s) => selectedSorteoIds.includes(s.id));
                  const toggleGroup = (e) => {
                    e.stopPropagation();
                    setSelectedSorteoIds((prev) => {
                      const ids = group.map((g) => g.id);
                      const allIn = ids.every((id) => prev.includes(id));
                      return allIn ? prev.filter((x) => !ids.includes(x)) : Array.from(new Set([...prev, ...ids]));
                    });
                  };
                  return (
                    <div
                      key={l.id}
                      className={`relative rounded-xl border-2 transition-colors overflow-hidden ${active ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/60"}`}
                      style={active ? { boxShadow: `inset 4px 0 0 ${l.color || "#1FB6A6"}` } : {}}
                      data-testid={`pos-lottery-card-${l.code}`}
                    >
                      <button
                        onClick={() => toggleLottery(l.id)}
                        data-testid={`pos-lottery-${l.code}`}
                        className={`w-full text-left px-3 ${lotteryView === "list" ? "py-1.5" : "py-2.5"}`}
                      >
                        {active && <Check size={14} className="absolute top-2 right-2 text-primary" />}
                        <div className="flex items-center gap-2">
                          <span
                            className={`grid place-items-center rounded-md border ${lotteryView === "list" ? "h-6 w-6" : "h-8 w-8"}`}
                            style={{ backgroundColor: (l.color || "#1FB6A6") + "26", color: l.color || "#1FB6A6", borderColor: (l.color || "#1FB6A6") + "55" }}
                          >
                            <Icon size={lotteryView === "list" ? 13 : 16} strokeWidth={2.5} />
                          </span>
                          <span className={`font-bold leading-tight ${lotteryView === "list" ? "text-xs" : "text-sm"}`}>{l.name}</span>
                        </div>
                        {lotteryView !== "list" && (
                          <div className="text-[11px] font-semibold text-muted-foreground mt-1">Paga {l.payout_multiplier}x · {l.number_length}d</div>
                        )}
                      </button>

                      {active && (
                        <div className="px-3 pb-3 pt-1 border-t-2 border-border/70 bg-background/40">
                          <div className="flex items-center justify-between mt-2 mb-1.5">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                              Horarios disponibles ({group.length})
                            </div>
                            {group.length > 0 && (
                              <button
                                type="button"
                                onClick={toggleGroup}
                                className="text-[10px] font-semibold text-primary hover:underline"
                                data-testid={`pos-toggle-sorteos-${l.code}`}
                              >
                                {allGroupSel ? "Quitar" : "Todos"}
                              </button>
                            )}
                          </div>
                          {group.length === 0 ? (
                            <div className="text-[11px] text-muted-foreground py-1.5">Sin horarios disponibles hoy.</div>
                          ) : (
                            <div className="grid grid-cols-2 gap-1.5">
                              {group.map((s) => {
                                const activeS = selectedSorteoIds.includes(s.id);
                                return (
                                  <button
                                    key={s.id}
                                    onClick={() => toggleSorteo(s.id)}
                                    data-testid={`pos-sorteo-${s.id}`}
                                    className={`rounded-md border-2 px-2 py-1.5 text-xs text-left transition-colors ${activeS ? "border-primary bg-primary/20 text-foreground" : "border-border hover:bg-secondary/60 text-muted-foreground"}`}
                                  >
                                    <div className="font-bold tabular-nums">{formatTime(s.scheduled_at)}</div>
                                    <div className="text-[10px] font-semibold truncate">{s.label || "Sorteo"}</div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          </Card>
        </div>

        {/* Panel central: jugadas */}
        <div className="md:col-span-1 lg:col-span-5 min-w-0 space-y-3">
          <Card className="bg-card border-2 border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Ticket actual</div>
                <div className="font-display font-bold text-sm">
                  {lotteryView === "list"
                    ? `${listSel.lotteries} lotería(s) · ${listSel.hours} horario(s)`
                    : `${selectedLotteryIds.length} lotería(s) · ${selectedSorteoIds.length} horario(s)`}
                </div>
              </div>
              <div className="flex gap-1.5 items-center" data-testid="pos-currency-selector">
                {currencyLocked && <Lock size={14} className="text-muted-foreground" title="Moneda bloqueada"/>}
                <button
                  onClick={() => changeCurrency("BS")}
                  disabled={currencyLocked && currency !== "BS"}
                  data-testid="pos-currency-bs"
                  className={`h-11 min-w-[56px] rounded-xl border-2 px-3 text-base font-extrabold transition-colors disabled:opacity-40 ${currency === "BS" ? "border-sky-500 bg-sky-600 text-white shadow-lg" : "border-border text-muted-foreground hover:bg-secondary/60"} ${flashKey === "f2" && currency === "BS" ? "ring-2 ring-sky-400" : ""}`}
                >Bs</button>
                <button
                  onClick={() => changeCurrency("USD")}
                  disabled={currencyLocked && currency !== "USD"}
                  data-testid="pos-currency-usd"
                  className={`h-11 min-w-[56px] rounded-xl border-2 px-3 text-base font-extrabold transition-colors disabled:opacity-40 ${currency === "USD" ? "border-emerald-500 bg-emerald-600 text-white shadow-lg" : "border-border text-muted-foreground hover:bg-secondary/60"} ${flashKey === "f2" && currency === "USD" ? "ring-2 ring-emerald-400" : ""}`}
                >USD</button>
                <button
                  onClick={() => changeCurrency("COP")}
                  disabled={currencyLocked && currency !== "COP"}
                  data-testid="pos-currency-cop"
                  className={`h-11 min-w-[56px] rounded-xl border-2 px-3 text-base font-extrabold transition-colors disabled:opacity-40 ${currency === "COP" ? "border-[#8f1433] bg-[#8f1433] text-white shadow-lg" : "border-border text-muted-foreground hover:bg-secondary/60"} ${flashKey === "f2" && currency === "COP" ? "ring-2 ring-rose-400" : ""}`}
                >COP</button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <button type="button" onClick={() => setActiveField("number")} className={`relative rounded-xl border-2 p-3 text-left transition-colors ${activeField === "number" ? "border-primary bg-primary/10 ring-2 ring-primary/50" : "border-border"}`} data-testid="pos-field-number">
                {activeField === "number" && (
                  <span className="absolute top-1.5 right-2 text-[10px] font-extrabold uppercase tracking-widest text-primary" data-testid="pos-field-number-active">activo</span>
                )}
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Número (N) · {numberLen}d</div>
                <div className={`font-mono tabular-nums font-bold min-h-[40px] break-all ${numberInput.length > 8 ? "text-lg" : "text-3xl"}`} data-testid="pos-current-number">{numberInput || "—"}</div>
              </button>
              <button type="button" onClick={() => setActiveField("amount")} className={`relative rounded-xl border-2 p-3 text-left transition-colors ${activeField === "amount" ? "border-primary bg-primary/10 ring-2 ring-primary/50" : "border-border"}`} data-testid="pos-field-amount">
                {activeField === "amount" && (
                  <span className="absolute top-1.5 right-2 text-[10px] font-extrabold uppercase tracking-widest text-primary" data-testid="pos-field-amount-active">activo</span>
                )}
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Monto (T) · {currency}{lastAmount ? ` · último: ${lastAmount}` : ""}</div>
                <div className="font-mono text-3xl tabular-nums font-bold min-h-[40px]" data-testid="pos-current-amount">{amountInput || "—"}</div>
              </button>
            </div>

            <div className="flex flex-col md:flex-row gap-2 mt-3">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Cliente (opcional)</Label>
                <Input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Nombre de referencia" data-testid="pos-customer-input" />
              </div>
              <Button type="button" variant="outline" onClick={() => setBurstMode((v) => !v)} data-testid="pos-burst-toggle" data-active={burstMode ? "true" : "false"}
                title="Ráfaga: fija el monto y agrega cada número al instante" className={`md:self-end ${burstMode ? "border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300" : ""}`}>
                <Zap size={15} className="mr-1.5" /> Ráfaga {burstMode ? "ON" : "OFF"}
              </Button>
              <Button onClick={() => addPlay()} className={`md:self-end ${flashKey === "add" ? "ring-2 ring-primary bg-primary/90" : ""}`} data-testid="pos-add-play-button" disabled={needsSession}>Agregar jugada (+)</Button>
            </div>
          </Card>

          {needsSign && (
            <Card className="bg-card border-border p-3" data-testid="pos-sign-selector">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-violet-700 dark:text-violet-300 mb-2">
                <Sparkles size={13} /> Signos zodiacales
                <button type="button" onClick={() => { if (signPinned) setSelectedSigns([]); setSignPinned(!signPinned); }} data-testid="pos-sign-pin-toggle" data-active={signPinned ? "true" : "false"}
                  title={signPinned ? "Signos fijados: se conservan entre jugadas" : "Fijar signo para jugadas consecutivas"}
                  className={`ml-auto inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-bold transition-colors ${signPinned ? "border-violet-500 bg-violet-500/25 text-violet-800 dark:text-violet-100" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                  {signPinned ? <Pin size={12} /> : <PinOff size={12} />} {signPinned ? "Fijado" : "Fijar"}
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-6 gap-1.5">
                {ZODIAC_SIGNS.map((sign) => {
                  const active = selectedSigns.includes(sign);
                  return (
                    <button
                      key={sign}
                      type="button"
                      onClick={() => toggleSign(sign)}
                      data-testid={`pos-sign-${sign}`}
                      data-active={active ? "true" : "false"}
                      className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors ${active ? "border-violet-500 bg-violet-500/20 text-violet-800 dark:text-violet-100" : "border-border hover:bg-secondary/60 text-muted-foreground"}`}
                    >
                      {sign}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">Puedes marcar varios signos: se crea una jugada por número × signo. Solo aplica a loterías zodiacales.{signPinned ? " Fijado: se mantienen hasta quitar el pin o cambiar de lotería." : " Se limpian tras cada jugada."}</div>
            </Card>
          )}

          {showQuickGrid && (
            <Card className="bg-card border-border p-3" data-testid="pos-quick-grid">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Selección rápida</div>
                <div className="text-[10px] font-semibold text-muted-foreground">{burstReady ? `Clic = agregar · ${amountInput} ${currency}` : "Clic = sumar a la lista"}</div>
              </div>
              <div className="grid grid-cols-7 sm:grid-cols-10 gap-1">
                {QUICK_ANIMALS.map((n) => (
                  <button key={n} type="button" onClick={() => pickQuickNumber(n)} data-testid={`pos-quick-num-${n}`}
                    className="rounded-md border border-border py-1.5 font-mono text-xs font-bold tabular-nums transition-colors hover:bg-primary/15 hover:border-primary active:scale-95">
                    {n}
                  </button>
                ))}
              </div>
            </Card>
          )}

          {selectedLotteryIds.length > 0 && (numberLen === 2 || numberLen === 3) && (
            <Card className="bg-card border-border p-3" data-testid="pos-series-selector">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Series</div>
                <div className="text-[10px] font-semibold text-muted-foreground">{burstReady ? `Clic = agregar la serie · ${amountInput} ${currency}` : "Clic = sumar la serie a la lista"}</div>
              </div>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
                {Array.from({ length: 10 }, (_, d) => {
                  const nums = seriesNumbers(d, numberLen);
                  return (
                    <button key={d} type="button" onClick={() => pickSeries(d)} data-testid={`pos-series-${d}`}
                      title={`${nums[0]} al ${nums[nums.length - 1]}`}
                      className="rounded-md border border-border py-1.5 font-mono text-xs font-bold tabular-nums transition-colors hover:bg-primary/15 hover:border-primary active:scale-95">
                      {numberLen === 3 ? `0${d}X` : `${d}X`}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">Serie {numberLen === 3 ? "06X = 060 al 069" : "6X = 60 al 69"}. Los números 0{numberLen === 3 ? ", 00 y 000" : " y 00"} no entran en ninguna serie.</div>
            </Card>
          )}

          <Card className="bg-card border-2 border-border p-3" data-testid="pos-play-slip">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                Jugadas <span className="text-foreground">({plays.length}/{SLIP_MAX_PLAYS})</span>
                {plays.length > SLIP_PAGE_SIZE && (
                  <span className="ml-2 text-[10px] font-semibold text-muted-foreground/90">
                    · Página {slipPage + 1} / {Math.min(SLIP_MAX_PAGES, Math.max(1, Math.ceil(plays.length / SLIP_PAGE_SIZE)))}
                  </span>
                )}
              </div>
              {plays.length > 0 && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={removeSelectedPlay} data-testid="pos-remove-selected-button">Eliminar (E)</Button>
                  <Button size="sm" variant="ghost" onClick={clearSlip} data-testid="pos-clear-slip-button">Limpiar (L)</Button>
                </div>
              )}
            </div>

            {plays.length > SLIP_PAGE_SIZE && (
              <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="pos-slip-pagination">
                {Array.from({ length: Math.min(SLIP_MAX_PAGES, Math.max(1, Math.ceil(plays.length / SLIP_PAGE_SIZE))) }).map((_, idx) => {
                  const start = idx * SLIP_PAGE_SIZE + 1;
                  const end = Math.min(plays.length, (idx + 1) * SLIP_PAGE_SIZE);
                  const isActive = idx === slipPage;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSlipPage(idx)}
                      data-testid={`pos-slip-page-${idx + 1}`}
                      className={`rounded-md border-2 px-2.5 py-1 text-[11px] font-bold tabular-nums transition-colors ${isActive ? "border-primary bg-primary/15 text-primary" : "border-border hover:bg-secondary/60 text-muted-foreground"}`}
                    >
                      #{idx + 1} · {start}-{end}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="max-h-[300px] overflow-y-auto" data-testid="pos-slip-scroll" style={{ scrollbarWidth: "thin" }}>
              {plays.length === 0 && (
                <div className="py-8 text-center text-sm font-semibold text-muted-foreground border-2 border-dashed border-border rounded-lg">
                  Sin jugadas. Selecciona loterías + horarios, escribe número + monto y agrega.
                </div>
              )}
              <div className="space-y-2">
                {plays.slice(slipPage * SLIP_PAGE_SIZE, slipPage * SLIP_PAGE_SIZE + SLIP_PAGE_SIZE).map((p, localIdx) => {
                  const i = slipPage * SLIP_PAGE_SIZE + localIdx;
                  const lot = lotteryMap[p.lottery_type_id];
                  const sor = sorteoMap[p.sorteo_id];
                  const isAgotado = agotadoKeys.has(playKey(p.sorteo_id, p.number));
                  const isSelected = selectedPlayIdx === i;
                  return (
                    <div key={i} onClick={() => setSelectedPlayIdx(i)} className={`flex items-center justify-between rounded-xl border-2 px-3 py-2 cursor-pointer transition-colors ${isAgotado ? "border-rose-500/70 bg-rose-500/10" : isSelected ? "border-primary bg-primary/10 ring-2 ring-primary/50" : "border-border bg-secondary/40 hover:bg-secondary/60"}`} data-testid="pos-play-item" data-selected={isSelected ? "true" : "false"}>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-12 grid place-items-center rounded-md font-mono text-lg font-bold border-2" style={{ backgroundColor: (lot?.color || "#1FB6A6") + "22", color: lot?.color || "#1FB6A6", borderColor: (lot?.color || "#1FB6A6") + "66" }}>{p.number}</div>
                        <div>
                          <div className="text-sm font-bold flex items-center gap-1.5">
                            <span className="text-muted-foreground/80 tabular-nums mr-1">#{i + 1}</span>
                            {lot?.name}
                            {p.sign && <Badge className="bg-violet-500/20 text-violet-800 dark:text-violet-200 border border-violet-500/40 text-[10px]" data-testid="pos-play-sign-badge">{p.sign}</Badge>}
                            {isAgotado && <Badge className="bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px]" data-testid="pos-play-agotado-badge">AGOTADO</Badge>}
                          </div>
                          <div className="text-[11px] font-semibold text-muted-foreground">{sor ? (formatTime(sor.scheduled_at) + (sor.label ? " · " + sor.label : "")) : "—"}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <SlipAmountInput
                          value={isUsd ? p.amount_usd : isCop ? p.amount_cop : p.amount_bs}
                          prefix={isUsd || isCop ? "$" : "Bs"}
                          onCommit={(v) => updatePlayAmount(i, v)}
                          className={isUsd ? "text-emerald-700 dark:text-emerald-300" : isCop ? "text-[#8f1433] dark:text-rose-300" : "text-sky-700 dark:text-sky-300"}
                          testId={`pos-play-amount-input-${i}`}
                        />
                      </div>
                      <Button variant="ghost" size="icon" className="ml-2 text-rose-300" onClick={(e) => { e.stopPropagation(); removePlay(i); }} data-testid={`pos-remove-play-${i}`}><Trash2 size={16} /></Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>

        {/* Panel derecho: totales + keypad */}
        <div className="md:col-span-2 lg:col-span-4 min-w-0 space-y-3">
          <Card className={`bg-card border-2 p-4 transition-colors ${curTheme.border}`}>
            <div className={`rounded-lg border-2 p-4 ${curTheme.border} ${curTheme.bgSoft}`} data-testid="pos-total-active">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Total a cobrar · {currency}</div>
              <div className={`font-display text-4xl md:text-5xl tabular-nums font-extrabold ${curTheme.text}`} data-testid={isUsd ? "pos-total-usd" : isCop ? "pos-total-cop" : "pos-total-bs"}>
                {isUsd ? `$ ${formatUsd(totals.usd)}` : isCop ? `$ ${formatCop(totals.cop)}` : formatBs(totals.bs)}
              </div>
              <div className="text-[11px] font-semibold text-muted-foreground mt-1">{isCop ? `Paridad Bs ${formatBs(copRateVal)} / COP` : `Tasa BCV ${formatBs(rateVal)}`} · {plays.length} jugada(s)</div>
            </div>
            <Button
              className={`w-full mt-3 h-14 text-lg font-extrabold ${curTheme.btn} ${flashKey === "f8" ? "ring-2 ring-offset-2 ring-offset-background " + curTheme.ring : ""}`}
              onClick={handleEmit}
              disabled={validating || sell.isPending || plays.length === 0 || needsSession || taqStatus?.suspended}
              data-testid="pos-confirm-sale-button"
            >
              <Send size={18} className="mr-2" /> {taqStatus?.suspended ? "Taquilla suspendida" : validating ? "Validando…" : "Emitir e imprimir ( + / F8)"}
            </Button>
          </Card>

          <Card className="bg-card border-2 border-border p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Teclado en pantalla</div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold text-muted-foreground">{showKeypad ? "Visible" : "Oculto"}</span>
                <Switch checked={showKeypad} onCheckedChange={setShowKeypad} data-testid="pos-keypad-toggle" />
              </div>
            </div>
            {showKeypad ? (
              <>
                <Keypad onKey={pressKey} onBackspace={pressBackspace} onClear={pressClear} onSeparator={pressSeparator} />
                <div className="mt-2 text-[11px] font-semibold text-muted-foreground text-center leading-relaxed" data-testid="pos-shortcuts-hint">
                  Pulsa <b className="text-foreground">F1</b> para ver todos los atajos · <b>Tab/Enter</b> alterna campo · <b>+</b> emite
                </div>
              </>
            ) : (
              <div className="py-4 text-center text-[11px] font-semibold text-muted-foreground" data-testid="pos-keypad-hidden-hint">
                Teclado oculto (modo teclado físico). Actívalo para pantalla táctil.
              </div>
            )}
          </Card>
        </div>
      </div>

      <TicketReceiptDialog ticket={receipt} open={!!receipt} onClose={closeReceipt} lotteryMap={lotteryMap} sorteoMap={receiptSorteoMap || sorteoMap} isReprint={receiptReprint} operatorName={user?.full_name || ""} />

      {/* Diálogo de disponibilidad ("agotado") ANTES de imprimir */}
      <Dialog open={availDialog.open} onOpenChange={(o) => setAvailDialog((prev) => ({ ...prev, open: o }))}>
        <DialogContent className="bg-card border-border" data-testid="pos-availability-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-300"><AlertTriangle size={18} /> Jugadas agotadas</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">Estas jugadas no tienen cupo disponible. Puedes quitarlas y emitir el resto sin perder el ticket.</div>
          <ScrollArea className="max-h-[240px] mt-2">
            <div className="space-y-2">
              {availDialog.results.map((r, i) => {
                const sor = sorteoMap[r.sorteo_id];
                const lot = sor ? lotteryMap[sor.lottery_type_id] : null;
                return (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2" data-testid="pos-availability-item">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-11 grid place-items-center rounded-md font-mono font-bold border border-rose-500/40 text-rose-200">{r.number}</div>
                      <div>
                        <div className="text-sm font-medium">{lot?.name || "Lotería"}</div>
                        <div className="text-[11px] text-muted-foreground">{sor ? formatTime(sor.scheduled_at) : ""} · {r.reason === "bloqueado" ? "Bloqueado" : "Agotado"}</div>
                      </div>
                    </div>
                    <div className="text-right text-[11px] text-muted-foreground">
                      Disp: <span className="font-mono text-rose-200">Bs {formatBs(r.remaining_bs ?? 0)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailDialog({ open: false, results: [] })} data-testid="pos-availability-review-button">Revisar</Button>
            <Button onClick={removeAgotadasAndEmit} data-testid="pos-availability-remove-emit-button">Quitar agotadas y emitir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repetir por serial */}
      <Dialog open={repeatOpen} onOpenChange={setRepeatOpen}>
        <DialogContent className="bg-card border-border" data-testid="pos-repeat-dialog">
          <DialogHeader><DialogTitle>Repetir jugada por serial</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input value={repeatCode} onChange={(e) => setRepeatCode(e.target.value)} placeholder="Serial del ticket (ej. ABCD-EFGH-JKL)" onKeyDown={(e) => e.key === "Enter" && findRepeat()} data-testid="pos-repeat-input" />
            <Button variant="outline" onClick={findRepeat} data-testid="pos-repeat-search-button"><Search size={16} /></Button>
          </div>
          {repeatTicket && (
            <div className="mt-2">
              <div className="text-xs text-muted-foreground mb-1">Ticket <CopyCode code={repeatTicket.code} /> · {repeatTicket.plays.length} jugadas</div>
              <ScrollArea className="max-h-[200px]">
                <div className="space-y-1.5">
                  {repeatTicket.plays.map((p, i) => {
                    const lot = lotteryMap[p.lottery_type_id];
                    return (
                      <div key={i} className="flex items-center justify-between rounded-md border border-border bg-secondary/40 px-2.5 py-1.5 text-sm">
                        <span className="font-mono font-bold">{p.number}</span>
                        <span className="text-xs text-muted-foreground">{lot?.name || p.lottery_type_id}</span>
                        <span className="font-mono tabular-nums text-xs">Bs {formatBs(p.amount_bs)}</span>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
              <div className="text-[11px] text-muted-foreground mt-2">Se cargarán al horario más próximo de cada lotería. Podrás ajustar horas/loterías antes de emitir.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRepeatOpen(false)}>Cancelar</Button>
            <Button onClick={loadRepeatPlays} disabled={!repeatTicket} data-testid="pos-repeat-load-button">Cargar jugadas</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anular por serial */}
      <Dialog open={voidSerialOpen} onOpenChange={setVoidSerialOpen}>
        <DialogContent className="bg-card border-border" data-testid="pos-void-serial-dialog">
          <DialogHeader><DialogTitle>Anular ticket por serial</DialogTitle></DialogHeader>
          <Input value={voidSerialCode} onChange={(e) => setVoidSerialCode(e.target.value)} placeholder="Serial del ticket" onKeyDown={(e) => e.key === "Enter" && voidBySerial.mutate()} data-testid="pos-void-serial-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidSerialOpen(false)}>Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-500" onClick={() => voidBySerial.mutate()} disabled={voidBySerial.isPending || !voidSerialCode.trim()} data-testid="pos-void-serial-confirm-button">Anular ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reimprimir por serial */}
      <Dialog open={reprintOpen} onOpenChange={setReprintOpen}>
        <DialogContent className="bg-card border-border" data-testid="pos-reprint-dialog">
          <DialogHeader><DialogTitle>Reimprimir ticket por serial</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input value={reprintCode} onChange={(e) => setReprintCode(e.target.value)} placeholder="Serial del ticket (ej. ABCD-EFGH-JKL)" onKeyDown={(e) => e.key === "Enter" && doReprint()} data-testid="pos-reprint-input" />
            <Button variant="outline" onClick={doReprint} data-testid="pos-reprint-search-button"><Search size={16} /></Button>
          </div>
          <div className="text-[11px] text-muted-foreground">Busca cualquier ticket histórico por su serial y abre el comprobante para reimprimirlo.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReprintOpen(false)}>Cancelar</Button>
            <Button onClick={doReprint} disabled={!reprintCode.trim()} data-testid="pos-reprint-confirm-button"><Printer size={15} className="mr-1.5" /> Buscar y reimprimir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Abrir caja */}
      <Dialog open={openSession} onOpenChange={setOpenSession}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Abrir caja</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Efectivo inicial (Bs)</Label>
              <Input type="number" value={sessionForm.opening_bs} onChange={(e) => setSessionForm({ ...sessionForm, opening_bs: e.target.value })} data-testid="pos-open-session-bs" />
            </div>
            <div className="space-y-2">
              <Label>Efectivo inicial (USD)</Label>
              <Input type="number" value={sessionForm.opening_usd} onChange={(e) => setSessionForm({ ...sessionForm, opening_usd: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Efectivo inicial (COP)</Label>
              <Input type="number" value={sessionForm.opening_cop} onChange={(e) => setSessionForm({ ...sessionForm, opening_cop: e.target.value })} data-testid="pos-open-session-cop" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenSession(false)}>Cancelar</Button>
            <Button onClick={() => openSess.mutate()} disabled={openSess.isPending} data-testid="pos-open-session-submit">Abrir caja</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cerrar caja — arqueo consolidado en Bs */}
      <CloseSessionDialog
        open={closeSession}
        onOpenChange={setCloseSession}
        session={session}
        rate={rate}
        form={sessionForm}
        setForm={setSessionForm}
        onSubmit={() => closeSess.mutate()}
        pending={closeSess.isPending}
      />

      {/* Confirmar anular último (F5) */}
      <Dialog open={confirmVoidLast} onOpenChange={setConfirmVoidLast}>
        <DialogContent className="bg-card border-border" data-testid="pos-confirm-void-last-dialog">
          <DialogHeader><DialogTitle className="text-rose-300 flex items-center gap-2"><Ban size={18} /> Anular último ticket</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            Se anulará el último ticket emitido de tu caja. Esta acción quedará registrada en el log de auditoría y no se puede deshacer.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmVoidLast(false)}>Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-500" onClick={() => { setConfirmVoidLast(false); voidLast.mutate(); }} disabled={voidLast.isPending} data-testid="pos-confirm-void-last-button">
              Sí, anular último
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ayuda F1 */}
      <ShortcutsHelpOverlay open={showHelp} onClose={() => setShowHelp(false)} />

      {/* Barra fija inferior con atajos (oculta con taquilla bloqueada) */}
      {!taqStatus?.suspended && <ShortcutStatusBar
        flashKey={flashKey}
        onHelp={() => setShowHelp(true)}
        onEmit={handleEmit}
        onAdd={addPlay}
        onClear={clearSlip}
        onCurrency={() => changeCurrency(currency === "BS" ? "USD" : "BS")}
        onRepeat={() => { setRepeatTicket(null); setRepeatCode(""); setRepeatOpen(true); }}
        onReprint={() => { setReprintCode(""); setReprintOpen(true); }}
        onVoidLast={() => setConfirmVoidLast(true)}
        onSession={() => {
          setSessionForm({ opening_bs: 0, opening_usd: 0, opening_cop: 0, closing_bs: 0, closing_usd: 0, closing_cop: 0, notes: "" });
          if (session) setCloseSession(true); else setOpenSession(true);
        }}
      />}
    </div>
  );
}
